import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Checkbox,
  Spinner,
} from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";

import { formatDate, parseWeekKey } from "@wc/domain/dates";

/**
 * @typedef {object} Goal
 * @property {string} id The goal id
 * @property {string} text The goal text
 * @property {boolean} completed Whether the goal is completed
 */

/**
 * @param {object} props Component props
 * @param {boolean} props.isOpen Whether the modal is open
 * @param {() => void} props.onClose Callback when modal should close
 * @param {Goal[]} props.goals Goals from the previous week
 * @param {string} props.weekOf The previous week's date key (yyyy-MM-dd)
 * @param {boolean} props.allComplete Whether all goals are already complete
 * @param {(goalId: string, completed: boolean) => void} props.onGoalToggle Callback when goal completion is toggled
 * @param {boolean} props.loading Whether data is still loading
 * @returns {React.ReactElement} The week review modal
 */
export default function WeekReviewModal({
  isOpen,
  onClose,
  goals,
  weekOf,
  allComplete,
  onGoalToggle,
  loading,
}) {
  const { t } = useTranslation();

  const weekDate = parseWeekKey(weekOf);
  const formattedDate = formatDate(weekDate);

  const handleOpenChange = (open) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      onClose={onClose}
      size="lg"
      isDismissable={true}
      isKeyboardDismissDisabled={false}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          {t("weekReview.title", { date: formattedDate })}
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {allComplete && (
                <div className="bg-success-50 text-success-700 dark:bg-success-900/20 dark:text-success-400 rounded-lg p-4 text-center">
                  {t("weekReview.allComplete")}
                </div>
              )}
              <div className="flex flex-col gap-3">
                {goals.map((goal) => (
                  <div key={goal.id} className="flex items-center gap-3">
                    <Checkbox
                      isSelected={goal.completed}
                      onValueChange={(completed) =>
                        onGoalToggle(goal.id, completed)
                      }
                      size="lg"
                      isDisabled={allComplete}
                    />
                    <span
                      className={`text-lg ${
                        goal.completed
                          ? "text-default-400 line-through"
                          : "text-foreground"
                      }`}
                    >
                      {goal.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onPress={onClose}>
            {t("weekReview.done")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
